import { useState } from "react";

import { Mail, MoreHorizontal, Trash2 } from "lucide-react";

import { Avatar } from "@/components/pieces/users/Avatar";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { MainProgram } from "@/types/programs";
import { NonNullableArrayItem } from "@/types/utils";

export type ProgramLead = NonNullableArrayItem<
  NonNullable<MainProgram["programOwners"]>
>;

interface LeadItemProps {
  data: ProgramLead;
  isAdmin?: boolean;
  allowRemove?: boolean;
  onRemoveProgramLead?: () => void;
  enableEmail?: boolean;
  loading?: boolean;
}

export function LeadItem({
  data,
  isAdmin,
  allowRemove,
  onRemoveProgramLead,
  enableEmail,
  loading,
}: LeadItemProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { user, summary } = data;
  const isEditable = isAdmin && allowRemove;
  const [isOpenRemoveDialog, setIsOpenRemoveDialog] = useState(false);

  const userLabel = user?.name || user?.email;

  return (
    <div className="flex items-center w-full mt-3">
      <div className="self-start">
        <Tooltip>
          <TooltipTrigger asChild>
            <div>
              <Avatar user={user} size={60} height="auto" borderColor="white" />
            </div>
          </TooltipTrigger>
          <TooltipContent side="left">{user?.email}</TooltipContent>
        </Tooltip>
      </div>

      <div className="mx-3 flex-1">
        <div className="text-base font-semibold max-w-[200px] break-words">
          {enableEmail ? (
            <a
              href={`mailto:${user?.email}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 hover:underline"
            >
              {userLabel} <Mail className="w-4 h-4" />
            </a>
          ) : (
            userLabel
          )}
        </div>
        <div className="mt-1 text-sm text-gray-600">
          {summary || user?.jobTitle}
        </div>
      </div>

      {isEditable && (
        <div>
          <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
            <DropdownMenuTrigger asChild>
              <Button
                variant="minimal"
                intent="secondary"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                variant="destructive"
                onClick={() => {
                  setIsOpenRemoveDialog(true);
                  setIsOpen(false);
                }}
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      <AlertDialog
        open={isOpenRemoveDialog}
        onOpenChange={setIsOpenRemoveDialog}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Program Lead</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove this program lead? This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={loading}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => onRemoveProgramLead?.()}
              disabled={loading}
              className="bg-red-600 hover:bg-red-700"
            >
              {loading ? "Removing..." : "Remove"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
